// SPDX-License-Identifier: GPL-3.0-only
// OpenSaga gameaiscript.cpp Action_CnxController/PlaySfx; aipathcnxhelper.cpp
// AIPathCnxControlSysUpdate/Create/SetOnRange. Original LEVEL.SCP uses the same
// bounded state VM as creature scripts, sharing the original message map.
import { EnemyScriptRuntime, insideAITrigger } from './enemies.mjs';

const flagValues = { block: 0x80000000, dbljump: 2, r2d2glide: 4, zipup: 8, usehatch: 16, jarjarjump: 32,
  hovertube: 64, swamp: 0x4000, takeover: 0x8000, vehicle: 0x10000, party: 0x20000, forgoodies: 0x80,
  forbaddies: 0x100, obstacle: 0x20000000, jump_now: 0x400 };
const validPosition = p => Array.isArray(p) && p.length === 3 && p.every(Number.isFinite);
const params = args => Object.fromEntries(args.filter(v => v.includes('=')).map(v => { const at = v.indexOf('='); return [v.slice(0, at).toLowerCase(), v.slice(at + 1)]; }));

export function createLevelAIController({ ai, script, scripts = new Map(), messages = new Map(), players,
  missionMode = false, forceComplete = () => undefined, getObjectState = () => undefined, onPlaySfx } = {}) {
  if (!ai || !script || typeof players !== 'function' || typeof missionMode !== 'boolean') throw new Error('Invalid original level AI inputs');
  const controllers = [], sounds = [], report = { blocked: [], limitations: [
    'Object-animation connection controllers only; other target kinds remain explicitly unsupported.',
    'Connection flags are exposed for navigation integration; this helper does not implement the native AI route solver.',
    'PlaySfx registers its authored repeated-sound request; actual playback requires caller audio binding.'
  ] };
  const seen = new Set(), note = reason => { if (!seen.has(reason)) { seen.add(reason); report.blocked.push({ reason }); } };
  const connections = ai.paths.map((path, pathIndex) => path.connections.map((connection, index) => ({ pathIndex, index,
    flagsA: connection.flagsA >>> 0, flagsB: connection.flagsB >>> 0, open: false })));
  function register(args) {
    const values = params(args), lower = args.map(v => v.toLowerCase());
    const from = values.from?.toLowerCase(), to = values.to?.toLowerCase();
    if (!from || !to) { note('CnxController missing authored endpoints'); return; }
    const unsupported = Object.keys(values).filter(key => !['from', 'to', 'pathname', 'obj', 'on_frames', 'on_flag', 'off_flag'].includes(key));
    if (unsupported.length || !values.obj) { note(`Unsupported CnxController target/parameters: ${unsupported.join(',') || 'no obj'}`); return; }
    let found;
    for (let pathIndex = 0; pathIndex < ai.paths.length && !found; pathIndex++) {
      const path = ai.paths[pathIndex]; if (values.pathname && path.name.toLowerCase() !== values.pathname.toLowerCase()) continue;
      const a = path.nodes.findIndex(node => node.name.toLowerCase() === from), b = path.nodes.findIndex(node => node.name.toLowerCase() === to);
      if (a < 0 || b < 0) continue;
      const index = path.connections.findIndex(c => c.nodeA === a && c.nodeB === b || c.nodeA === b && c.nodeB === a);
      if (index >= 0) found = { connection: connections[pathIndex][index], direction: path.connections[index].nodeA === a ? 0 : 1 };
    }
    if (!found) { note(`Missing authored CnxController connection ${from}->${to}`); return; }
    const parseFlag = name => {
      if (!name) return 0;
      if (['obstacle_open', 'obstacle_closed'].includes(name.toLowerCase())) return 0;
      const flag = flagValues[name.toLowerCase()]; if (flag === undefined) note(`Unsupported original AI connection flag ${name}`);
      return flag ?? 0;
    };
    const controller = { ...found, object: values.obj.toLowerCase(), from, to, bothWays: lower.includes('bothways'), checkVisible: lower.includes('checkvisible'),
      onFlags: parseFlag(values.on_flag), offFlags: parseFlag(values.off_flag),
      onOpen: values.on_flag?.toLowerCase() === 'obstacle_open', offOpen: values.off_flag?.toLowerCase() === 'obstacle_open',
      range: null, on: null };
    if (values.on_frames) {
      const range = /^(lastframe(?:-1)?|\d+)to(lastframe(?:-1)?|\d+)$/i.exec(values.on_frames);
      if (!range) { note(`Invalid original CnxController on_frames ${values.on_frames}`); return; }
      controller.range = range.slice(1).map(v => v.toLowerCase());
    }
    controllers.push(controller);
  }
  const vm = new EnemyScriptRuntime(script, { scripts, messages,
    condition(name, args) {
      const list = players();
      if (!Array.isArray(list)) throw new Error('Invalid original level player list');
      if (name === 'missionmode') return missionMode;
      if (name === 'forcecomplete') return forceComplete(args[0]);
      if (name === 'eitherplayeris') {
        if (list.some(p => typeof p.name !== 'string')) return undefined;
        return list.some(p => p.name.toLowerCase() === args[0]?.toLowerCase());
      }
      if (name === 'eitherplayerintriggerarea') {
        if (!args[0]) return false;
        const area = ai.areas.find(area => area.name === args[0].toLowerCase());
        if (!area) return undefined;
        return list.some(player => validPosition(player.position) && insideAITrigger(area, player.position));
      }
      return undefined;
    },
    action(name, args, { firstTime }) {
      if (name === 'cnxcontroller') { if (firstTime) register(args); return true; }
      if (name === 'playsfx') {
        if (firstTime) {
          const p = params(args), value = (key, fallback) => p[key] === undefined ? fallback : Number(p[key]);
          const request = { name: p.name, playCount: value('playcount', 1), repeatDelay: value('repdelay', 0), startDelay: value('startdelay', 0), position: null };
          if (!request.name || ![request.playCount, request.repeatDelay, request.startDelay].every(Number.isFinite)) throw new Error('Invalid original PlaySfx request');
          if (['x', 'y', 'z'].some(key => p[key] !== undefined)) {
            request.position = ['x', 'y', 'z'].map(key => value(key, 1e9));
            if (!validPosition(request.position)) throw new Error('Invalid original PlaySfx position');
          }
          if (p.character_pos !== undefined) { note('Original PlaySfx named character position unbound'); return true; }
          sounds.push(request); if (onPlaySfx) onPlaySfx(request); else note(`Original audio playback unbound: ${request.name}`);
        }
        return true; // Native PlaySfx schedules repeat audio and returns1.
      }
      return undefined;
    } });
  function tick(dt) {
    const result = vm.tick(dt);
    for (const blocked of result.blocked) note(`${blocked.kind}: ${blocked.name}`);
    for (const controller of controllers) {
      const state = getObjectState(controller.object);
      if (!state) { note(`Original CnxController object state unbound: ${controller.object}`); continue; }
      let on = true;
      if (controller.checkVisible && typeof state.visible !== 'boolean') { note(`Original CnxController visibility unbound: ${controller.object}`); continue; }
      if (controller.checkVisible && !state.visible) on = false;
      else {
        if (!Number.isFinite(state.frame) || !Number.isFinite(state.endFrame)) { note(`Original CnxController animation frame unbound: ${controller.object}`); continue; }
        const end = Math.min(1024, Math.trunc(state.endFrame));
        const frame = state.frame > 0 ? Math.min(1024, Math.trunc(state.frame), Math.trunc(state.endFrame)) - 1 : 0;
        const resolve = text => text === 'lastframe' ? end : text === 'lastframe-1' ? end - 1 : Number(text);
        if (!controller.range) on = false; // Original zero-filled on_frames bitset.
        else { const [first, last] = controller.range.map(resolve); on = frame >= Math.max(0, first - 1) && frame < Math.min(last, end); }
      }
      controller.on = on;
      const connection = controller.connection, selected = controller.direction ? 'flagsB' : 'flagsA', reverse = controller.direction ? 'flagsA' : 'flagsB';
      const remove = on ? controller.offFlags : controller.onFlags, add = on ? controller.onFlags : controller.offFlags;
      connection[selected] = ((connection[selected] & ~remove) | add) >>> 0;
      if (controller.bothWays) connection[reverse] = ((connection[reverse] & ~remove) | add) >>> 0;
      connection.open = on ? controller.onOpen : controller.offOpen;
    }
    return result;
  }
  return { tick, vm, messages, controllers, connections, sounds, report };
}
